import {LinkExternalIcon} from '@primer/octicons-react'
import {Box, Text, Link, Button} from '@primer/react'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants'
import {ProjectItemSectionFieldsLoading} from './ProjectItemSectionFieldsLoading'
import {ProjectItemSectionFieldsInternal} from './ProjectItemSectionFields'
import {Suspense, useCallback} from 'react'
import type {ProjectItemSectionFieldListQuery} from './__generated__/ProjectItemSectionFieldListQuery.graphql'
import {useToastContext} from '@github-ui/toast'
import {commitUnArchiveProjectItem} from '../../mutations/unarchive-project-item'

export type ProjectItemSectionFieldListProps = {
  projectId: string
  projectItemId: string
  isArchived: boolean
  onIssueUpdate?: () => void
}

export function ProjectItemSectionFieldList(props: ProjectItemSectionFieldListProps) {
  return (
    <Box as="li" sx={{py: 0, mt: 0, mb: 2, listStyleType: 'none'}}>
      <Box as="ul" sx={{listStyle: 'none'}}>
        <Suspense fallback={<ProjectItemSectionFieldsLoading />}>
          <ProjectItemSectionFieldListInternal {...props} />
        </Suspense>
      </Box>
    </Box>
  )
}

function ProjectItemSectionFieldListInternal({
  isArchived,
  projectItemId,
  projectId,
  onIssueUpdate,
}: ProjectItemSectionFieldListProps) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {node} = useLazyLoadQuery<ProjectItemSectionFieldListQuery>(
    graphql`
      query ProjectItemSectionFieldListQuery($id: ID!) {
        node(id: $id) {
          ... on ProjectV2Item {
            project {
              viewerCanUpdate
              url
              id
            }
            ...ProjectItemSectionFields
          }
        }
      }
    `,
    {id: projectItemId},
  )

  const restore = useCallback(() => {
    commitUnArchiveProjectItem({
      environment,
      input: {projectId, itemId: projectItemId},
      onCompleted: () => {
        addToast({type: 'success', message: LABELS.archivedSuccess})
      },
      onError: () => {
        addToast({type: 'error', message: LABELS.archivedError})
      },
    })
  }, [environment, projectId, projectItemId, addToast])

  if (!node || !node.project) return null
  const canRestore = isArchived && node.project.viewerCanUpdate
  return (
    <>
      {canRestore && (
        <Box as="li" sx={{px: 3, mt: 1}}>
          <Text sx={{fontSize: 0, color: 'fg.muted', display: 'block'}}>
            {LABELS.archivedDescription}{' '}
            <Link onClick={restore} sx={{cursor: 'pointer'}}>
              Restore
            </Link>
          </Text>
        </Box>
      )}
      <ProjectItemSectionFieldsInternal onIssueUpdate={onIssueUpdate} projectItem={node} />
      {/* isOpen is passed by the issue viewer memex so we dont need this section */}
      <Box as="li" sx={{px: 3}}>
        <Button size="small" trailingVisual={LinkExternalIcon} as={Link} target="_blank" href={node.project.url}>
          Open project
        </Button>
      </Box>
    </>
  )
}

try{ ProjectItemSectionFieldList.displayName ||= 'ProjectItemSectionFieldList' } catch {}
try{ ProjectItemSectionFieldListInternal.displayName ||= 'ProjectItemSectionFieldListInternal' } catch {}